import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import intlShape from '@/shared/utils/intlShape';
import classnames from 'classnames';

import Toggle from 'react-toggle';
import UserSearch from '@/shared/components/input/UserSearch';
import { CapacityTaskSelect } from '@/shared/components/input/filterInputs/FilterCapacityTaskSelect';

import messages from '../hrmMessages';
import { FaUndo } from 'react-icons/fa';
import DelayedDateRangePicker from '@/shared/components/input/DelayedDateRangePicker';
import PeriodButtonGroup, { getPeriodStartEndDates } from '@/shared/components/PeriodButtonGroup';
import moment from 'moment';
import { isBeforeMinimum } from '@/shared/utils/validationHelpers';
import ToastrService from '@/shared/services/ToastrService';

const periods = [
  'week',
  'nextWeek',
  'month',
  'nextMonth',
  'quarter-1',
  'quarter',
  'nextQuarter',
  'year-1',
  'year',
  'year+1',
];

export const defaultFilters = {
  deadlineFrom: moment.utc().startOf('year'),
  deadlineUntil: moment
    .utc()
    .endOf('year')
    .startOf('day'),
  period: 'year',
  mainTaskTemplateId: undefined,
  displayMode: 0,
  groupByMonth: false,
};

const maxDate = moment
  .utc()
  .add(1, 'year')
  .endOf('year');
const defaultDeadlineFrom = () => moment.utc().startOf('year');
const defaultDeadlineUntil = () =>
  moment
    .utc()
    .endOf('year')
    .startOf('day');
const isOutsideRange = date => isBeforeMinimum(date) || moment(date).isAfter(maxDate, 'day');

class CapacityReportFilters extends Component {
  constructor(props) {
    super(props);
    this.state = { hasFilters: this.computeHasFilters(props.filters) };
  }

  computeHasFilters = filters => {
    const dateFromChanged =
      !!filters.deadlineFrom && !!defaultFilters.deadlineFrom
        ? !moment(filters.deadlineFrom).isSame(defaultFilters.deadlineFrom, 'day')
        : filters.deadlineFrom !== defaultFilters.deadlineFrom;

    const dateUntilChanged =
      !!filters.deadlineUntil && !!defaultFilters.deadlineUntil
        ? !moment(filters.deadlineUntil).isSame(defaultFilters.deadlineUntil, 'day')
        : filters.deadlineUntil !== defaultFilters.deadlineUntil;

    return (
      dateFromChanged ||
      dateUntilChanged ||
      filters.period !== defaultFilters.period ||
      filters.employeeId ||
      filters.employeeProfile ||
      filters.mainTaskTemplateId !== defaultFilters.mainTaskTemplateId ||
      filters.displayMode !== defaultFilters.displayMode ||
      filters.groupByMonth !== defaultFilters.groupByMonth
    );
  };

  isAtLeastOneMonth = (from, until) => {
    if (!from || !until) return false;
    return moment(until).diff(moment(from), 'months') >= 1;
  };

  filterChanged = filter => {
    const { filters, onFilterChange } = this.props;
    const newFilters = { ...filters, ...filter };

    if (moment.isMoment(newFilters.deadlineFrom)) {
      newFilters.deadlineFrom = moment.utc(newFilters.deadlineFrom.format('YYYY-MM-DD'));
    }
    if (moment.isMoment(newFilters.deadlineUntil)) {
      newFilters.deadlineUntil = moment.utc(newFilters.deadlineUntil.format('YYYY-MM-DD'));
    }

    if (!this.isAtLeastOneMonth(newFilters.deadlineFrom, newFilters.deadlineUntil)) {
      newFilters.groupByMonth = false;
    }

    const hasFilters = this.computeHasFilters(newFilters);

    if (JSON.stringify(filters) !== JSON.stringify(newFilters)) {
      onFilterChange(newFilters);
    }

    this.setState({ hasFilters });
  };

  resetFilters = () => {
    this.filterChanged({ ...defaultFilters });
  };

  dateRangeChanged = (startDate, endDate) => {
    const { intl, filters } = this.props;

    if (!startDate && !endDate) {
      this.filterChanged({
        period: 'year',
        deadlineFrom: defaultDeadlineFrom(),
        deadlineUntil: defaultDeadlineUntil(),
      });
      return;
    }

    const maxStr = maxDate.format('YYYY-MM-DD');
    const startStr = startDate ? moment(startDate).format('YYYY-MM-DD') : null;
    const endStr = endDate ? moment(endDate).format('YYYY-MM-DD') : null;

    if (!startStr || !endStr || startStr > maxStr || endStr > maxStr) {
      const [periodFrom, periodUntil] = getPeriodStartEndDates(filters.period || 'year');
      ToastrService.error(intl.formatMessage(messages.CapacityMaxDateWarning));
      this.filterChanged({
        period: filters.period || 'year',
        deadlineFrom: periodFrom || defaultDeadlineFrom(),
        deadlineUntil: periodUntil || defaultDeadlineUntil(),
      });
      return;
    }

    this.filterChanged({ deadlineFrom: startDate, deadlineUntil: endDate });
  };

  render() {
    const { filters, columns, intl, availableMainTaskTemplateIds } = this.props;
    const { hasFilters } = this.state;

    return (
      <div className="info-block container-fluid">
        <div className="row">
          <div className="col-lg-3 col-md-6">
            <div className="form-group">
              <DelayedDateRangePicker
                startDate={filters.deadlineFrom ? moment(filters.deadlineFrom) : undefined}
                startDateId="CapacityReportFilterDeadlineFrom"
                endDate={filters.deadlineUntil ? moment(filters.deadlineUntil) : undefined}
                endDateId="CapacityReportFilterDeadlineUntil"
                onDatesChange={({ startDate, endDate }) =>
                  this.dateRangeChanged(startDate, endDate)
                }
                intl={intl}
                startDatePlaceholderText={intl.formatMessage(messages.DeadlineFrom)}
                endDatePlaceholderText={intl.formatMessage(messages.DeadlineUntil)}
                isOutsideRange={isOutsideRange}
                showClearDates={false}
                minimumNights={6}
                block
              />
            </div>
          </div>
          <div className="col-lg-9">
            <div className="form-group">
              <PeriodButtonGroup
                dateFrom={filters.deadlineFrom ? moment(filters.deadlineFrom) : undefined}
                dateUntil={filters.deadlineUntil ? moment(filters.deadlineUntil) : undefined}
                period={filters.period}
                onChange={({ dateFrom, dateUntil, period }) =>
                  this.filterChanged({ deadlineFrom: dateFrom, deadlineUntil: dateUntil, period })
                }
                visiblePeriods={periods}
              />
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-lg-3 col-md-6">
            <div className="form-group">
              <CapacityTaskSelect
                initialValue={filters.mainTaskTemplateId}
                onChange={value =>
                  this.filterChanged({
                    mainTaskTemplateId: value || undefined,
                  })
                }
                availableTemplateIds={availableMainTaskTemplateIds}
                placeholder={intl.formatMessage(messages.AllMainTasks)}
              />
            </div>
          </div>
          <div className="col-lg-3 col-md-6">
            <div className="form-group">
              <label className="margin5R">
                <FormattedMessage {...messages.CapacityDisplayMode} />:
              </label>
              <div role="group" className="btn-group">
                <button
                  type="button"
                  onClick={() => this.filterChanged({ displayMode: 0 })}
                  title={intl.formatMessage(messages.CapacityDisplayModeDuration)}
                  className={classnames('btn btn-default', {
                    active: (filters.displayMode ?? 0) === 0,
                  })}
                >
                  {intl.formatMessage(messages.CapacityDisplayModeDuration)}
                </button>
                <button
                  type="button"
                  onClick={() => this.filterChanged({ displayMode: 1 })}
                  title={intl.formatMessage(messages.CapacityDisplayModeTaskCount)}
                  className={classnames('btn btn-default', {
                    active: (filters.displayMode ?? 0) === 1,
                  })}
                >
                  {intl.formatMessage(messages.CapacityDisplayModeTaskCount)}
                </button>
                <button
                  type="button"
                  onClick={() => this.filterChanged({ displayMode: 2 })}
                  title={intl.formatMessage(messages.CapacityDisplayModeRelationCount)}
                  className={classnames('btn btn-default', {
                    active: (filters.displayMode ?? 0) === 2,
                  })}
                >
                  {intl.formatMessage(messages.CapacityDisplayModeRelationCount)}
                </button>
              </div>
            </div>
          </div>
          <div className="col-lg-3 col-md-6">
            <div className="form-group">
              <label className="react-toggle-wrapper margin10T">
                <Toggle
                  checked={filters.groupByMonth || false}
                  disabled={!this.isAtLeastOneMonth(filters.deadlineFrom, filters.deadlineUntil)}
                  onChange={e => this.filterChanged({ groupByMonth: e.target.checked })}
                />
                <span className="label-text">
                  <FormattedMessage {...messages.CapacityGroupByMonth} />
                </span>
              </label>
            </div>
          </div>
        </div>
        {hasFilters && (
          <div className="row">
            <div className="col-lg-4 col-md-6">
              <button
                type="button"
                className="btn btn-default btn-reset"
                onClick={() => this.resetFilters()}
              >
                <FaUndo className="react-icon" />
                &nbsp;
                <FormattedMessage {...messages.ResetFilters} />
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }
}

CapacityReportFilters.propTypes = {
  intl: intlShape.isRequired,
  filters: PropTypes.shape({
    deadlineFrom: PropTypes.string,
    deadlineUntil: PropTypes.string,
    period: PropTypes.string,
    mainTaskTemplateId: PropTypes.string,
    displayMode: PropTypes.number,
    groupByMonth: PropTypes.bool,
  }),
  onFilterChange: PropTypes.func.isRequired,
  availableMainTaskTemplateIds: PropTypes.arrayOf(PropTypes.string),
};

CapacityReportFilters.defaultProps = {
  filters: { ...defaultFilters },
};
export default injectIntl(CapacityReportFilters);
